import React from 'react';
import { useParams } from 'react-router-dom';
import { Code2, Smartphone, Cloud, Shield, Database, Cpu, Rocket, ArrowRight, CheckCircle2, Globe, Layers, Terminal, Package, Award, Users, TrendingUp, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

// COMPREHENSIVE SERVICE DETAILS
const serviceDetails = {
  'web-development': {
    icon: Code2,
    title: 'Web Development',
    description: 'Custom web applications built with cutting-edge technologies',
    longDescription: 'Transform your ideas into powerful web applications with our expert development team. We specialize in creating responsive, scalable, and high-performance websites that drive business growth.',
    features: [
      'Responsive & Mobile-First Design',
      'Progressive Web Apps (PWA)',
      'Single Page Applications (SPA)',
      'E-commerce Solutions',
      'Content Management Systems',
      'API Integration & Development',
      'Performance Optimization',
      'SEO-Friendly Architecture'
    ],
    technologies: ['React', 'Next.js', 'Node.js', 'TypeScript', 'Tailwind CSS'],
    benefits: [
      { title: 'Fast Performance', desc: '99% faster load times' },
      { title: 'Scalable', desc: 'Handles millions of users' },
      { title: 'Secure', desc: 'Bank-level encryption' }
    ]
  },
  'ui-ux-design': {
    icon: Smartphone,
    title: 'UI/UX Design',
    description: 'User-centered design that delivers exceptional experiences',
    longDescription: 'Create intuitive and engaging user experiences that delight your customers. Our design team combines aesthetics with functionality to build interfaces that users love.',
    features: [
      'User Research & Personas',
      'Wireframing & Prototyping',
      'Visual Design & Branding',
      'Usability Testing',
      'Design Systems',
      'Interactive Prototypes',
      'Accessibility Compliance',
      'Design Workshops'
    ],
    technologies: ['Figma', 'Adobe XD', 'Sketch', 'InVision', 'Framer'],
    benefits: [
      { title: 'User Satisfaction', desc: '95% user approval rate' },
      { title: 'Conversion Boost', desc: '40% increase in conversions' },
      { title: 'Brand Recognition', desc: 'Memorable experiences' }
    ]
  },
  'mobile-apps': {
    icon: Smartphone,
    title: 'Mobile App Development',
    description: 'Native and cross-platform mobile applications',
    longDescription: 'Build powerful mobile applications for iOS and Android that engage users and drive business results. From concept to deployment, we handle everything.',
    features: [
      'iOS & Android Development',
      'React Native & Flutter',
      'App Store Optimization',
      'Push Notifications',
      'Offline Functionality',
      'In-App Purchases',
      'Location-Based Services',
      'Real-time Sync'
    ],
    technologies: ['React Native', 'Flutter', 'Swift', 'Kotlin', 'Firebase'],
    benefits: [
      { title: 'Cross-Platform', desc: 'One codebase, two platforms' },
      { title: 'Native Performance', desc: 'Smooth 60fps animations' },
      { title: 'Quick Launch', desc: '50% faster development' }
    ]
  },
  'ai-integration': {
    icon: Cpu,
    title: 'AI Integration',
    description: 'Intelligent solutions powered by artificial intelligence',
    longDescription: 'Leverage the power of artificial intelligence to automate processes, gain insights, and deliver personalized experiences. Our AI solutions are tailored to your business needs.',
    features: [
      'Machine Learning Models',
      'Natural Language Processing',
      'Computer Vision',
      'Predictive Analytics',
      'Chatbots & Virtual Assistants',
      'Recommendation Engines',
      'Sentiment Analysis',
      'Voice Recognition'
    ],
    technologies: ['TensorFlow', 'PyTorch', 'OpenAI', 'Hugging Face', 'AWS AI'],
    benefits: [
      { title: 'Automation', desc: '80% task automation' },
      { title: 'Accuracy', desc: '99% prediction accuracy' },
      { title: 'Cost Savings', desc: '60% operational cost reduction' }
    ]
  },
  'cybersecurity': {
    icon: Shield,
    title: 'Cybersecurity',
    description: 'Comprehensive security solutions to protect your digital assets',
    longDescription: 'Protect your business from cyber threats with our comprehensive security solutions. We implement industry-leading practices to safeguard your data and infrastructure.',
    features: [
      'Security Audits & Assessments',
      'Penetration Testing',
      'Data Encryption',
      'Compliance Management',
      '24/7 Security Monitoring',
      'Incident Response',
      'Vulnerability Scanning',
      'Security Training'
    ],
    technologies: ['AWS Security', 'Cloudflare', 'Let\'s Encrypt', 'OAuth 2.0', 'JWT'],
    benefits: [
      { title: 'Zero Breaches', desc: '100% threat prevention' },
      { title: 'Compliance', desc: 'GDPR, HIPAA, SOC 2' },
      { title: 'Peace of Mind', desc: '24/7 monitoring' }
    ]
  },
  'cloud-solutions': {
    icon: Cloud,
    title: 'Cloud Solutions',
    description: 'Scalable cloud infrastructure and migration services',
    longDescription: 'Move to the cloud with confidence. We help businesses migrate, optimize, and manage cloud infrastructure for maximum performance and cost efficiency.',
    features: [
      'AWS, Azure & Google Cloud',
      'Cloud Migration',
      'DevOps & CI/CD',
      'Serverless Architecture',
      'Cloud Cost Optimization',
      'Auto-Scaling',
      'Disaster Recovery',
      'Multi-Cloud Strategy'
    ],
    technologies: ['AWS', 'Azure', 'Google Cloud', 'Kubernetes', 'Docker'],
    benefits: [
      { title: 'Scalability', desc: 'Handle 10x traffic spikes' },
      { title: 'Reliability', desc: '99.99% uptime SLA' },
      { title: 'Cost Efficient', desc: '40% cost reduction' }
    ]
  }
};

// TECHNOLOGY STACK DETAILS
const technologyDetails = {
  'frontend': {
    icon: Code2,
    title: 'Frontend Technologies',
    description: 'Modern frontend frameworks and tools for building stunning user interfaces',
    longDescription: 'Master the art of frontend development with our expertise in modern JavaScript frameworks, CSS preprocessors, and build tools.',
    features: [
      'React.js & Next.js Development',
      'Vue.js & Nuxt.js Solutions',
      'Angular Enterprise Apps',
      'TypeScript Integration',
      'Responsive Design (Tailwind CSS)',
      'State Management (Redux, Zustand)',
      'Component Libraries',
      'Performance Optimization'
    ],
    technologies: ['React', 'Vue', 'Angular', 'TypeScript', 'Tailwind CSS', 'Webpack'],
    benefits: [
      { title: 'Fast Development', desc: '50% faster build time' },
      { title: 'Modern UI', desc: 'Beautiful animations' },
      { title: 'Maintainable', desc: 'Clean code architecture' }
    ]
  },
  'backend': {
    icon: Database,
    title: 'Backend Technologies',
    description: 'Robust backend systems with powerful APIs and databases',
    longDescription: 'Build scalable backend systems with our expertise in server-side technologies, databases, and API development.',
    features: [
      'Node.js & Express.js APIs',
      'Python (Django, FastAPI)',
      'Java Spring Boot',
      'GraphQL & REST APIs',
      'PostgreSQL & MongoDB',
      'Redis Caching',
      'Message Queues (RabbitMQ)',
      'Microservices Architecture'
    ],
    technologies: ['Node.js', 'Python', 'Java', 'PostgreSQL', 'MongoDB', 'Redis'],
    benefits: [
      { title: 'High Performance', desc: '1000+ req/sec' },
      { title: 'Scalable', desc: 'Handle millions of users' },
      { title: 'Secure', desc: 'Enterprise-grade security' }
    ]
  },
  'devops': {
    icon: Terminal,
    title: 'DevOps & CI/CD',
    description: 'Automated deployment pipelines and infrastructure management',
    longDescription: 'Streamline your development workflow with automated CI/CD pipelines, containerization, and infrastructure as code.',
    features: [
      'Docker Containerization',
      'Kubernetes Orchestration',
      'GitHub Actions & GitLab CI',
      'Infrastructure as Code (Terraform)',
      'Automated Testing',
      'Blue-Green Deployments',
      'Monitoring & Logging',
      'Auto-Scaling Solutions'
    ],
    technologies: ['Docker', 'Kubernetes', 'GitHub Actions', 'Terraform', 'Prometheus'],
    benefits: [
      { title: 'Fast Deployment', desc: '10x faster releases' },
      { title: 'Zero Downtime', desc: 'Continuous delivery' },
      { title: 'Automation', desc: '90% automated workflow' }
    ]
  },
  'mobile': {
    icon: Smartphone,
    title: 'Mobile Technologies',
    description: 'Cross-platform and native mobile development solutions',
    longDescription: 'Create amazing mobile experiences with modern frameworks that work on both iOS and Android platforms.',
    features: [
      'React Native Development',
      'Flutter Apps',
      'Native iOS (Swift)',
      'Native Android (Kotlin)',
      'Mobile Backend Integration',
      'Push Notifications',
      'Offline-First Architecture',
      'App Store Deployment'
    ],
    technologies: ['React Native', 'Flutter', 'Swift', 'Kotlin', 'Firebase'],
    benefits: [
      { title: 'Cross-Platform', desc: 'iOS + Android in one' },
      { title: 'Native Feel', desc: '60fps performance' },
      { title: 'Faster Launch', desc: '40% time savings' }
    ]
  }
};

// INDUSTRY SOLUTIONS
const industryDetails = {
  'finance': {
    icon: TrendingUp,
    title: 'Finance & Banking',
    description: 'Secure fintech solutions for modern banking',
    longDescription: 'Transform financial services with secure, compliant, and innovative technology solutions designed for banks, fintech startups, and financial institutions.',
    features: [
      'Payment Gateway Integration',
      'Mobile Banking Apps',
      'Fraud Detection Systems',
      'Trading Platforms',
      'Wealth Management Tools',
      'Blockchain Integration',
      'Regulatory Compliance',
      'Real-time Analytics'
    ],
    technologies: ['Stripe', 'Plaid', 'Blockchain', 'AWS', 'Python'],
    benefits: [
      { title: 'Security', desc: 'PCI DSS compliant' },
      { title: 'Fast Transactions', desc: '<2 second processing' },
      { title: 'Compliance', desc: 'SOC 2, GDPR ready' }
    ]
  },
  'healthcare': {
    icon: Shield,
    title: 'Healthcare Solutions',
    description: 'HIPAA-compliant healthcare technology',
    longDescription: 'Improve patient care with secure healthcare software solutions including telemedicine, EHR systems, and patient portals.',
    features: [
      'Telemedicine Platforms',
      'Electronic Health Records (EHR)',
      'Patient Portals',
      'Appointment Scheduling',
      'Medical Billing Systems',
      'Lab Integration',
      'HIPAA Compliance',
      'Medical IoT'
    ],
    technologies: ['HL7', 'FHIR', 'React', 'Node.js', 'MongoDB'],
    benefits: [
      { title: 'HIPAA Compliant', desc: '100% secure' },
      { title: 'Patient Care', desc: '50% better outcomes' },
      { title: 'Efficiency', desc: '60% time savings' }
    ]
  },
  'ecommerce': {
    icon: Package,
    title: 'E-Commerce Solutions',
    description: 'Scalable online store platforms',
    longDescription: 'Build high-converting e-commerce platforms with seamless checkout, inventory management, and personalized shopping experiences.',
    features: [
      'Custom Shopping Carts',
      'Payment Processing',
      'Inventory Management',
      'Order Fulfillment',
      'Customer Accounts',
      'Product Recommendations',
      'Multi-vendor Marketplaces',
      'Analytics Dashboard'
    ],
    technologies: ['Shopify', 'WooCommerce', 'Stripe', 'React', 'Node.js'],
    benefits: [
      { title: 'Conversions', desc: '45% higher sales' },
      { title: 'Scalability', desc: 'Handle Black Friday traffic' },
      { title: 'Mobile Sales', desc: '70% mobile-optimized' }
    ]
  },
  'education': {
    icon: Users,
    title: 'Education & E-Learning',
    description: 'Digital learning platforms',
    longDescription: 'Create engaging online learning experiences with LMS platforms, virtual classrooms, and interactive course content.',
    features: [
      'Learning Management Systems',
      'Virtual Classrooms',
      'Video Streaming',
      'Assessment Tools',
      'Student Progress Tracking',
      'Certification Management',
      'Interactive Content',
      'Mobile Learning Apps'
    ],
    technologies: ['React', 'WebRTC', 'AWS', 'MongoDB', 'Socket.io'],
    benefits: [
      { title: 'Engagement', desc: '80% completion rate' },
      { title: 'Accessibility', desc: 'Learn anywhere, anytime' },
      { title: 'Scalability', desc: '10,000+ concurrent users' }
    ]
  }
};

const allServices = [
  { id: 1, ...serviceDetails['web-development'], path: 'web-development' },
  { id: 2, ...serviceDetails['ui-ux-design'], path: 'ui-ux-design' },
  { id: 3, ...serviceDetails['mobile-apps'], path: 'mobile-apps' },
  { id: 4, ...serviceDetails['ai-integration'], path: 'ai-integration' },
  { id: 5, ...serviceDetails['cybersecurity'], path: 'cybersecurity' },
  { id: 6, ...serviceDetails['cloud-solutions'], path: 'cloud-solutions' }
];

export const Services = () => {
  const { service: serviceParam, tech: techParam, industry: industryParam } = useParams();
  
  // Determine which type of page to show
  const currentService = serviceParam ? serviceDetails[serviceParam] : null;
  const currentTech = techParam ? technologyDetails[techParam] : null;
  const currentIndustry = industryParam ? industryDetails[industryParam] : null;
  const currentItem = currentService || currentTech || currentIndustry;
  
  const IconComponent = currentItem ? currentItem.icon : Rocket;

  if (currentItem) {
    // Individual detail page with FULL content and animations
    return (
      <div className="min-h-screen pt-24 pb-16">
        <div className="container mx-auto px-6">
          {/* Hero Section with Animation */}
          <div className="max-w-4xl mx-auto text-center mb-16 animate-slide-up">
            <div className="w-24 h-24 bg-[rgba(0,168,255,0.2)] rounded-2xl flex items-center justify-center mx-auto mb-8 animate-pulse" style={{ boxShadow: '0 0 40px rgba(0,168,255,0.6)' }}>
              <IconComponent className="text-[#00a8ff]" size={56} style={{ filter: 'drop-shadow(0 0 15px #00a8ff)' }} />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold led-text animate-led-pulse mb-6" style={{ textShadow: '0 0 25px #00a8ff, 0 0 50px #4cd3ff' }}>
              {currentItem.title}
            </h1>
            <p className="text-2xl text-[#00a8ff] mb-4" style={{ textShadow: '0 0 12px #00a8ff' }}>
              {currentItem.description}
            </p>
            <p className="text-lg text-white max-w-3xl mx-auto">
              {currentItem.longDescription}
            </p>
          </div>

          {/* Features Grid with Animation */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto mb-16">
            {currentItem.features.map((feature, idx) => (
              <Card key={idx} className="bg-[rgba(30,64,98,0.7)] border-2 border-[#00a8ff] rounded-xl hover:shadow-[0_0_40px_#00a8ff] transition-all duration-500 hover:-translate-y-3 hover:scale-105" style={{ boxShadow: '0 0 15px rgba(0,168,255,0.3)', animationDelay: `${idx * 100}ms` }}>
                <CardContent className="p-6 flex items-center space-x-4">
                  <CheckCircle2 className="text-[#00a8ff] flex-shrink-0 animate-pulse" size={28} style={{ filter: 'drop-shadow(0 0 10px #00a8ff)' }} />
                  <span className="text-white text-lg font-semibold led-text" style={{ textShadow: '0 0 8px #00a8ff' }}>{feature}</span>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Technologies Section */}
          <div className="mb-16 max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold led-text text-center mb-8" style={{ textShadow: '0 0 20px #00a8ff' }}>Technologies We Use</h2>
            <div className="flex flex-wrap justify-center gap-4">
              {currentItem.technologies.map((tech, idx) => (
                <div key={idx} className="bg-[rgba(0,168,255,0.2)] border-2 border-[#00a8ff] px-6 py-3 rounded-xl font-bold text-[#00a8ff] text-lg hover:bg-[#00a8ff] hover:text-[#0a2540] transition-all duration-300 hover:scale-110" style={{ boxShadow: '0 0 15px rgba(0,168,255,0.4)' }}>
                  {tech}
                </div>
              ))}
            </div>
          </div>

          {/* Benefits Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-16">
            {currentItem.benefits.map((benefit, idx) => (
              <Card key={idx} className="bg-gradient-to-br from-[rgba(0,168,255,0.3)] to-[rgba(0,168,255,0.1)] border-3 border-[#00a8ff] rounded-2xl hover:shadow-[0_0_50px_#00a8ff] transition-all duration-500 hover:-translate-y-4 hover:scale-105" style={{ boxShadow: '0 0 25px rgba(0,168,255,0.5)' }}>
                <CardContent className="p-8 text-center">
                  <Award className="text-[#00a8ff] mx-auto mb-4 animate-pulse" size={48} style={{ filter: 'drop-shadow(0 0 15px #00a8ff)' }} />
                  <h3 className="text-2xl font-bold text-white mb-3 led-text" style={{ textShadow: '0 0 10px #00a8ff' }}>{benefit.title}</h3>
                  <p className="text-[#00a8ff] text-lg font-semibold" style={{ textShadow: '0 0 8px #00a8ff' }}>{benefit.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* CTA */}
          <div className="text-center animate-slide-up">
            <Link to="/contact">
              <Button className="bg-gradient-to-r from-[#00a8ff] to-[#4cd3ff] hover:from-[#4cd3ff] hover:to-[#00a8ff] text-[#0a2540] font-bold px-12 py-8 text-2xl transition-all duration-300 hover:scale-110 rounded-2xl" style={{ boxShadow: '0 0 40px #00a8ff' }}>
                Get Started with {currentItem.title}
                <ArrowRight className="ml-3" size={28} />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // All services page
  return (
    <div className="min-h-screen pt-24 pb-16">
      <div className="container mx-auto px-6">
        {/* Hero */}
        <div className="max-w-4xl mx-auto text-center mb-16 animate-slide-up">
          <h1 className="text-5xl md:text-6xl font-bold led-text animate-led-pulse mb-6" style={{ textShadow: '0 0 20px #00a8ff, 0 0 40px #4cd3ff' }}>
            OUR SERVICES
          </h1>
          <p className="text-xl text-[#00a8ff]" style={{ textShadow: '0 0 10px #00a8ff' }}>
            Comprehensive technology solutions designed to accelerate your business growth
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {allServices.map((service, idx) => (
            <Link key={service.id} to={`/services/${service.path}`}>
              <Card className="group bg-[rgba(30,64,98,0.7)] border-2 border-[#00a8ff] rounded-xl hover:shadow-[0_0_40px_#00a8ff] transition-all duration-300 hover:-translate-y-4 cursor-pointer h-full" style={{ boxShadow: '0 0 15px rgba(0,168,255,0.3)' }}>
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-[rgba(0,168,255,0.2)] rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#00a8ff] group-hover:scale-110 transition-all duration-300" style={{ boxShadow: '0 0 20px rgba(0,168,255,0.5)' }}>
                    <service.icon className="text-[#00a8ff] group-hover:text-[#0a2540] transition-colors" size={32} />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4 led-text group-hover:text-[#00a8ff] transition-colors" style={{ textShadow: '0 0 10px #00a8ff' }}>
                    {service.title}
                  </h3>
                  <p className="text-[#00a8ff] mb-6" style={{ textShadow: '0 0 5px #00a8ff' }}>
                    {service.description}
                  </p>
                  <div className="text-[#00a8ff] font-bold flex items-center group-hover:translate-x-2 transition-transform">
                    Learn More <ArrowRight className="ml-2" size={18} />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* CTA Section */}
        <div className="text-center bg-[rgba(30,64,98,0.7)] border-2 border-[#00a8ff] rounded-2xl p-12 animate-slide-up" style={{ boxShadow: '0 0 30px rgba(0,168,255,0.3)' }}>
          <h2 className="text-4xl font-bold led-text mb-6" style={{ textShadow: '0 0 15px #00a8ff' }}>
            Let's Build Something Amazing
          </h2>
          <p className="text-[#00a8ff] text-xl mb-8" style={{ textShadow: '0 0 8px #00a8ff' }}>
            Get in touch to discuss your project requirements
          </p>
          <Link to="/contact">
            <Button className="bg-[#00a8ff] text-[#0a2540] hover:bg-[#4cd3ff] font-bold px-8 py-6 text-lg transition-all duration-300 hover:scale-110" style={{ boxShadow: '0 0 30px #00a8ff' }}>
              Contact Us Today
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};
